import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ClipboardList,
  PlusCircle,
  Send,
  Loader2,
  AlertCircle,
  Package,
  ScanBarcode,
  Info,
  Plus,
  Minus,
  Wand2,
  TrendingDown,
} from "lucide-react";

import Modal from "@/components/ui/modal";
import AddSerialsModal from "./AddSerialsModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ReactSelect } from "@/components/ui/react-select";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";

const reasonOptions = [
  { value: "damaged", label: "Damaged" },
  { value: "expired", label: "Expired" },
  { value: "stolen", label: "Stolen" },
  { value: "lost", label: "Lost" },
  { value: "found", label: "Found" },
  { value: "opening_balance", label: "Opening Balance" },
  { value: "physical_count", label: "Physical Count" },
  { value: "system_error", label: "System Error" },
  { value: "customer_return", label: "Customer Return" },
  { value: "supplier_return", label: "Supplier Return" },
  { value: "write_off", label: "Write Off" },
  { value: "promotion", label: "Promotion" },
  { value: "sample", label: "Sample" },
  { value: "transfer_error", label: "Transfer Error" },
  { value: "other", label: "Other" },
];

const ProductStockAdjustmentModal = ({
  open,
  onOpenChange,
  product,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);
  const [isFetchingAdjItem, setIsFetchingAdjItem] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [activeSerialIndex, setActiveSerialIndex] = useState(null);

  const [newAdjustmentData, setNewAdjustmentData] = useState({
    store_id: "",
    reason: "other",
    notes: "",
    items: [],
    status: "draft",
    requires_approval: true,
  });

  useEffect(() => {
    if (!open || !product) {
      return;
    }

    const storeId = currentStore?.id || "";
    setNewAdjustmentData({
      store_id: storeId,
      reason: "other",
      notes: "",
      items: [],
      status: "draft",
      requires_approval: true,
    });
    setShowSerialModal(false);
    setActiveSerialIndex(null);

    const fetchProductDetails = async () => {
      setIsFetchingAdjItem(true);
      try {
        const searchParam = product.sku || product.name;
        let posProduct = null;
        try {
          const posRes = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
            params: {
              search: searchParam,
              per_page: 20,
              store_id: storeId || undefined,
              with: [
                "brand",
                "model",
                "unit",
                "categories",
                "batches",
                "serial_numbers",
              ],
            },
          });

          posProduct =
            posRes.data?.data?.find(
              (prod) =>
                String(prod.id) === String(product.id) ||
                (product.sku && prod.sku === product.sku)
            ) ||
            posRes.data?.data?.find((prod) => prod.name === product.name) ||
            null;
        } catch {
          // silent fallback
        }

        // Fallback: If not found in search results, try direct POS product details by ID
        if (!posProduct && product.id && API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS) {
          try {
            const detailRes = await apiClient.get(
              API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(product.id)
            );
            if (detailRes.data?.data) {
              posProduct = detailRes.data.data;
            }
          } catch {
            // silent fallback
          }
        }

        let inventoryId =
          posProduct?.inventory_id ||
          posProduct?.batches?.[0]?.inventory_id ||
          posProduct?.serial_numbers?.[0]?.inventory_id ||
          product.inventory_id ||
          null;

        const productId = posProduct?.id || product.id;
        const productName = posProduct?.name || product.name;
        const sku = posProduct?.sku || product.sku;
        const trackingType =
          posProduct?.tracking_type || product.tracking_type || "none";
        const unitCost = parseFloat(posProduct?.cost ?? product.cost ?? 0);
        let sellableQty = posProduct?.sellable_qty || 0;

        // If inventoryId is still missing, check the inventory by product endpoint
        if (!inventoryId && productId) {
          try {
            const invRes = await apiClient.get(
              API_ENDPOINTS.INVENTORY.BY_PRODUCT(productId)
            );
            const inventories = Array.isArray(invRes.data?.data)
              ? invRes.data.data
              : invRes.data?.data
                ? [invRes.data.data]
                : [];
            const match = storeId
              ? inventories.find(
                (inv) => String(inv.store_id) === String(storeId)
              )
              : inventories[0];
            if (match?.id) {
              inventoryId = match.id;
              if (match.quantity !== undefined && match.quantity !== null) {
                sellableQty = parseFloat(match.quantity) || 0;
              }
            }
          } catch {
            // silent fallback
          }
        }

        const newItem = {
          product_id: productId,
          product_name: productName,
          sku: sku,
          inventory_id: inventoryId,
          inventory_batch_id: null,
          adjustment_quantity: 0,
          current_quantity: sellableQty,
          unit_cost: unitCost,
          reason_notes: "",
          tracking_type: trackingType,
          available_batches: posProduct?.batches || [],
          available_serials: posProduct?.serial_numbers || [],
          serial_numbers: [],
        };

        // If batches are empty in POS payload, fetch them explicitly
        if (
          newItem.tracking_type === "standard" &&
          newItem.available_batches.length === 0 &&
          storeId
        ) {
          try {
            const res = await apiClient.get(
              API_ENDPOINTS.INVENTORY.BATCHES_AVAILABLE(
                productId,
                storeId
              )
            );
            newItem.available_batches = res.data?.data || [];
          } catch {
            /* silent */
          }
        }

        if (newItem.available_batches?.length === 1 && !newItem.inventory_batch_id) {
          newItem.inventory_batch_id = newItem.available_batches[0].id.toString();
          newItem.current_quantity = newItem.available_batches[0].quantity;
          if (newItem.available_batches[0].unit_cost) {
            newItem.unit_cost = newItem.available_batches[0].unit_cost;
          }
        }

        // If serials are empty in POS payload, fetch them explicitly
        if (
          newItem.tracking_type === "serial" &&
          newItem.available_serials.length === 0 &&
          storeId
        ) {
          try {
            const res = await apiClient.get(
              API_ENDPOINTS.INVENTORY.SERIALS_AVAILABLE,
              {
                params: {
                  product_id: productId,
                  store_id: storeId,
                },
              }
            );
            newItem.available_serials = res.data?.data || [];
          } catch {
            /* silent */
          }
        }

        setNewAdjustmentData((prev) => ({
          ...prev,
          reason: inventoryId
            ? prev.reason && prev.reason !== "opening_balance"
              ? prev.reason
              : "physical_count"
            : "opening_balance",
          items: [newItem],
        }));
      } catch (err) {
        console.error("Failed to setup product details for adjustment", err);
        let inventoryId = product.inventory_id || null;
        if (!inventoryId && product.id) {
          try {
            const invRes = await apiClient.get(
              API_ENDPOINTS.INVENTORY.BY_PRODUCT(product.id)
            );
            const inventories = Array.isArray(invRes.data?.data)
              ? invRes.data.data
              : invRes.data?.data
                ? [invRes.data.data]
                : [];
            const match = storeId
              ? inventories.find(
                (inv) => String(inv.store_id) === String(storeId)
              )
              : inventories[0];
            if (match?.id) {
              inventoryId = match.id;
            }
          } catch {
            // silent fallback
          }
        }
        const newItem = {
          product_id: product.id,
          product_name: product.name,
          sku: product.sku,
          inventory_id: inventoryId,
          inventory_batch_id: null,
          adjustment_quantity: 0,
          current_quantity: 0,
          unit_cost: parseFloat(product.cost || 0),
          reason_notes: "",
          tracking_type: product.tracking_type || "none",
          available_batches: [],
          available_serials: [],
          serial_numbers: [],
        };
        setNewAdjustmentData((prev) => ({
          ...prev,
          reason: inventoryId
            ? prev.reason && prev.reason !== "opening_balance"
              ? prev.reason
              : "physical_count"
            : "opening_balance",
          items: [newItem],
        }));
      } finally {
        setIsFetchingAdjItem(false);
      }
    };

    fetchProductDetails();
  }, [open, product, currentStore?.id]);

  const saveAdjustmentMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await apiClient.post(API_ENDPOINTS.STOCK.ADJUSTMENTS, payload);
      return res.data?.data;
    },
    onSuccess: async (data, variables) => {
      toast.success("Stock adjustment created successfully");
      if (variables.shouldSubmit && data?.id) {
        try {
          await apiClient.post(API_ENDPOINTS.STOCK.SUBMIT(data.id), {});
          toast.success("Stock adjustment submitted for approval");
        } catch {
          // handled
        }
      }
      onOpenChange(false);
      setNewAdjustmentData({
        store_id: currentStore?.id || "",
        reason: "other",
        notes: "",
        items: [],
        status: "draft",
        requires_approval: true,
      });

      queryClient.invalidateQueries(["products"]);
      queryClient.invalidateQueries(["stockAdjustments"]);
      queryClient.invalidateQueries(["all-pos-products"]);
      if (product?.id && currentStore?.id) {
        queryClient.invalidateQueries([
          "stock-adj-history",
          product.id,
          currentStore.id,
        ]);
        queryClient.invalidateQueries([
          "batches",
          product.id,
          currentStore.id,
        ]);
      }
      onSuccess?.(data);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to create stock adjustment"
      );
    },
  });

  const saveOpeningStockMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await apiClient.post(
        API_ENDPOINTS.STOCK.OPENING_BALANCE,
        payload
      );
      return res.data?.data;
    },
    onSuccess: async (data) => {
      toast.success("Opening stock created successfully");
      onOpenChange(false);
      setNewAdjustmentData({
        store_id: currentStore?.id || "",
        reason: "other",
        notes: "",
        items: [],
        status: "draft",
        requires_approval: true,
      });

      queryClient.invalidateQueries(["products"]);
      queryClient.invalidateQueries(["stockAdjustments"]);
      queryClient.invalidateQueries(["all-pos-products"]);
      if (product?.id && currentStore?.id) {
        queryClient.invalidateQueries([
          "stock-adj-history",
          product.id,
          currentStore.id,
        ]);
        queryClient.invalidateQueries([
          "batches",
          product.id,
          currentStore.id,
        ]);
      }
      onSuccess?.(data);
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to create opening stock"
      );
    },
  });

  const handleAdjItemChange = (index, field, value) => {
    const newItems = [...newAdjustmentData.items];
    const item = { ...newItems[index] };
    if (field === "inventory_batch_id") {
      item.inventory_batch_id = value;
      const selectedBatch = item.available_batches.find(
        (b) => b.id.toString() === value?.toString()
      );
      if (selectedBatch) {
        item.current_quantity = selectedBatch.quantity;
        item.unit_cost = selectedBatch.unit_cost;
      }
    } else if (field === "serial_numbers") {
      item.serial_numbers = value;
      if (
        item.tracking_type === "serial" &&
        (!item.adjustment_quantity || item.adjustment_quantity === 0)
      ) {
        const isNegative = item.adjustment_quantity < 0;
        const validCount = value.filter((s) => s.trim() !== "").length;
        item.adjustment_quantity = isNegative ? -validCount : validCount;
      }
    } else {
      item[field] = value;
    }
    newItems[index] = item;
    setNewAdjustmentData({ ...newAdjustmentData, items: newItems });
  };

  const handleSaveAdjustment = (shouldSubmit = false) => {
    if (!newAdjustmentData.store_id) {
      toast.error("Store is not selected. Please select a store first.");
      return;
    }
    if (newAdjustmentData.items.length === 0) {
      toast.error("Please add at least one product item");
      return;
    }
    const hasZeroQty = newAdjustmentData.items.every(
      (item) => (parseInt(item.adjustment_quantity) || 0) === 0
    );
    if (hasZeroQty) {
      toast.error("Please enter a non-zero adjustment quantity");
      return;
    }

    const item = newAdjustmentData.items[0];
    const hasInventoryRecord = Boolean(item.inventory_id);

    // If it has inventory record, perform normal stock adjustment
    if (hasInventoryRecord) {
      if (newAdjustmentData.reason === "opening_balance") {
        toast.error(
          "Opening stock can only be added if there are no existing inventory records. Please select a valid adjustment reason."
        );
        return;
      }

      const missingBatchItem = newAdjustmentData.items.find(
        (it) =>
          (it.tracking_type === "standard" || (it.available_batches && it.available_batches.length > 0)) &&
          !it.inventory_batch_id
      );
      if (missingBatchItem) {
        toast.error(
          `Please select a batch for "${missingBatchItem.product_name}". Batch is required for stock adjustments.`
        );
        return;
      }

      for (const it of newAdjustmentData.items) {
        if (it.tracking_type === "serial") {
          const targetQty = Math.abs(parseInt(it.adjustment_quantity) || 0);
          if (targetQty === 0) {
            toast.error(
              `Adjustment quantity for "${it.product_name}" cannot be 0`,
            );
            return;
          }
          const validSerials = (it.serial_numbers || []).filter(
            (s) => s && typeof s === "string" && s.trim() !== "",
          );
          if (validSerials.length !== targetQty) {
            toast.error(
              `Number of serial numbers (${validSerials.length}) must match adjustment quantity (${targetQty}) for "${it.product_name}"`,
            );
            return;
          }
        }
      }

      const payload = {
        store_id: newAdjustmentData.store_id,
        reason: newAdjustmentData.reason,
        notes: newAdjustmentData.notes || null,
        requires_approval: newAdjustmentData.requires_approval,
        status: "draft",
        shouldSubmit,
        items: newAdjustmentData.items.map((it) => ({
          product_id: it.product_id,
          inventory_id: it.inventory_id,
          inventory_batch_id: it.inventory_batch_id || null,
          current_quantity: parseInt(it.current_quantity) || 0,
          adjustment_quantity: parseInt(it.adjustment_quantity) || 0,
          unit_cost: parseFloat(it.unit_cost) || 0,
          reason_notes: it.reason_notes || null,
          serial_numbers: (it.serial_numbers || []).filter(
            (s) => s && typeof s === "string" && s.trim() !== ""
          ),
        })),
      };
      saveAdjustmentMutation.mutate(payload);
      return;
    }

    // If it doesn't have an inventory record, record that adjustment as opening stock
    const qty = parseInt(item.adjustment_quantity) || 0;
    if (qty <= 0) {
      toast.error("Opening stock quantity must be greater than 0");
      return;
    }

    if (item.tracking_type === "serial") {
      const validSerials = (item.serial_numbers || []).filter(
        (s) => s && typeof s === "string" && s.trim() !== ""
      );
      if (validSerials.length !== qty) {
        toast.error(
          `Number of serial numbers (${validSerials.length}) must match opening quantity (${qty})`
        );
        return;
      }
    }

    const openingPayload = {
      store_id: newAdjustmentData.store_id,
      notes: newAdjustmentData.notes || "Initial opening stock",
      items: [
        {
          product_id: item.product_id,
          opening_quantity: qty,
          unit_cost:
            item.unit_cost !== undefined &&
              item.unit_cost !== null &&
              item.unit_cost !== ""
              ? parseFloat(item.unit_cost)
              : null,
          reorder_level: item.reorder_level
            ? parseInt(item.reorder_level)
            : null,
          location: null,
          sku: item.sku || null,
          reason_notes:
            item.reason_notes ||
            newAdjustmentData.notes ||
            "Initial opening stock",
          serial_numbers: (item.serial_numbers || []).filter(
            (s) => s && typeof s === "string" && s.trim() !== ""
          ),
          meta: null,
        },
      ],
    };
    saveOpeningStockMutation.mutate(openingPayload);
  };

  const currentItem = newAdjustmentData.items[0];
  const hasInventory = Boolean(currentItem?.inventory_id);

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title={hasInventory ? "New Stock Adjustment" : "Initialize Opening Stock"}
        icon={ClipboardList}
        maxWidth="max-w-[90vw]"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {hasInventory ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleSaveAdjustment(false)}
                  disabled={
                    saveAdjustmentMutation.isPending ||
                    newAdjustmentData.items.length === 0
                  }
                  className="gap-2"
                >
                  {saveAdjustmentMutation.isPending &&
                    !saveAdjustmentMutation.variables?.shouldSubmit ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PlusCircle className="h-4 w-4" />
                  )}
                  Save as Draft
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSaveAdjustment(true)}
                  disabled={
                    saveAdjustmentMutation.isPending ||
                    newAdjustmentData.items.length === 0
                  }
                  className="gap-2"
                >
                  {saveAdjustmentMutation.isPending &&
                    saveAdjustmentMutation.variables?.shouldSubmit ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Save & Submit
                </Button>
              </>
            ) : (
              <Button
                variant="default"
                onClick={() => handleSaveAdjustment(true)}
                disabled={
                  saveOpeningStockMutation.isPending ||
                  newAdjustmentData.items.length === 0
                }
                className="gap-2"
              >
                {saveOpeningStockMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <PlusCircle className="h-4 w-4" />
                )}
                Save Opening Stock
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-6">
          {!hasInventory && !isFetchingAdjItem && currentItem && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300 rounded-md border border-amber-200 dark:border-amber-800/50 text-xs">
              <Info className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <span>
                This product has no existing inventory record for this store yet. This adjustment will be recorded as <strong>Opening Stock</strong> to initialize the inventory.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason *</label>
              <ReactSelect
                options={
                  hasInventory
                    ? reasonOptions.filter((o) => o.value !== "opening_balance")
                    : reasonOptions.filter((o) => o.value === "opening_balance")
                }
                value={reasonOptions.find(
                  (o) => o.value === newAdjustmentData.reason
                )}
                isDisabled={!hasInventory}
                onChange={(val) =>
                  setNewAdjustmentData({
                    ...newAdjustmentData,
                    reason: val?.value || "physical_count",
                  })
                }
                placeholder="Select Reason"
              />
              {!hasInventory && (
                <p className="text-[11px] text-amber-600 font-medium flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3 w-3" />
                  Opening stock is required because there are no existing inventory records for this product.
                </p>
              )}
              {hasInventory &&
                [
                  "physical_count",
                  "found",
                  "customer_return",
                ].includes(newAdjustmentData.reason) && (
                  <p className="text-[11px] text-blue-600 font-medium flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" />
                    This adjustment type will be automatically processed once approved.
                  </p>
                )}
            </div>

            <div className="md:col-span-1 space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Internal notes about this adjustment..."
                value={newAdjustmentData.notes}
                onChange={(e) =>
                  setNewAdjustmentData({
                    ...newAdjustmentData,
                    notes: e.target.value,
                  })
                }
                className="min-h-[80px]"
              />
            </div>

            <div className="flex items-center space-x-2 py-2">
              <Switch
                id="modal-adj-requires-approval"
                checked={newAdjustmentData.requires_approval}
                onCheckedChange={(checked) =>
                  setNewAdjustmentData((prev) => ({
                    ...prev,
                    requires_approval: checked,
                  }))
                }
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="modal-adj-requires-approval"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Requires Approval
                </Label>
                <p className="text-xs text-muted-foreground">
                  {newAdjustmentData.requires_approval
                    ? "This adjustment will need to be approved before processing."
                    : "This adjustment will be automatically approved upon submission."}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Adjustment Items
              </h3>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground min-w-[200px]">
                      Product
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground min-w-[150px]">
                      Tracking Info
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                      Current Qty
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground w-[120px]">
                      Adj (+/-)
                    </th>
                    <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground w-[120px]">
                      Unit Cost
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                      Item Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {isFetchingAdjItem ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-muted-foreground"
                      >
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <Loader2 className="h-6 w-6 animate-spin text-primary" />
                          <p className="text-sm">Fetching product details...</p>
                        </div>
                      </td>
                    </tr>
                  ) : newAdjustmentData.items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-muted-foreground"
                      >
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <Package className="h-6 w-6 text-muted-foreground/50" />
                          <p className="text-sm">
                            No items found or could not be loaded.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    newAdjustmentData.items.map((item, index) => (
                      <tr key={index} className="hover:bg-muted/20">
                        <td className="px-4 py-3">
                          <div className="font-medium">{item.product_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {item.sku}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {item.tracking_type === "standard" ? (
                            <div className="space-y-1">
                              <ReactSelect
                                options={item.available_batches.map((batch) => ({
                                  value: batch.id.toString(),
                                  label: `${batch.batch_number} (Qty: ${batch.quantity})`,
                                }))}
                                value={
                                  item.available_batches.find(
                                    (batch) =>
                                      batch.id.toString() ===
                                      item.inventory_batch_id?.toString()
                                  )
                                    ? {
                                      value: item.inventory_batch_id.toString(),
                                      label: `${item.available_batches.find(
                                        (b) =>
                                          b.id.toString() ===
                                          item.inventory_batch_id?.toString()
                                      ).batch_number
                                        } (Qty: ${item.available_batches.find(
                                          (b) =>
                                            b.id.toString() ===
                                            item.inventory_batch_id?.toString()
                                        ).quantity
                                        })`,
                                    }
                                    : null
                                }
                                onChange={(option) =>
                                  handleAdjItemChange(
                                    index,
                                    "inventory_batch_id",
                                    option?.value
                                  )
                                }
                                placeholder={
                                  item.available_batches?.length > 0
                                    ? "Select Batch *"
                                    : "No existing batches"
                                }
                                isClearable={false}
                                isDisabled={item.available_batches?.length === 0}
                                className="w-full"
                              />
                              {!item.inventory_batch_id &&
                                item.available_batches?.length > 0 && (
                                  <p className="text-[10px] text-destructive font-medium flex items-center gap-1 mt-1">
                                    <AlertCircle className="h-3 w-3" />
                                    Batch is required for stock adjustments
                                  </p>
                                )}
                              {item.available_batches?.length === 0 && (
                                <p className="text-[10px] text-amber-600 font-medium flex items-center gap-1 mt-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {hasInventory
                                    ? "No batches found for this product inventory."
                                    : "No batches found. Stock will be initialized as Opening Balance."}
                                </p>
                              )}
                            </div>
                          ) : item.tracking_type === "serial" ? (
                            <div className="space-y-1">
                              <Badge
                                variant="outline"
                                className="text-[10px] bg-primary/5 text-blue-600 border-primary/20"
                              >
                                <ScanBarcode className="h-3 w-3 mr-1" />
                                Serial Tracked
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full h-8 justify-between text-xs font-normal px-2"
                                onClick={() => {
                                  setActiveSerialIndex(index);
                                  setShowSerialModal(true);
                                }}
                              >
                                {item.serial_numbers &&
                                  item.serial_numbers.length > 0 ? (
                                  <>
                                    <span className="truncate">
                                      {item.serial_numbers.length} /{" "}
                                      {Math.abs(item.adjustment_quantity || 0)}{" "}
                                      Serials
                                    </span>
                                    <Info className="h-3 w-3 opacity-50" />
                                  </>
                                ) : (
                                  <>
                                    <span>
                                      0 /{" "}
                                      {Math.abs(item.adjustment_quantity || 0)}{" "}
                                      Serials
                                    </span>
                                    <Plus className="h-3 w-3 opacity-50" />
                                  </>
                                )}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground px-2">
                              Not tracked
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-mono">
                          {item.current_quantity}
                        </td>
                        <td className="px-4 py-3 w-[160px]">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={() =>
                                handleAdjItemChange(
                                  index,
                                  "adjustment_quantity",
                                  (item.adjustment_quantity || 0) - 1
                                )
                              }
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <Input
                              type="number"
                              value={item.adjustment_quantity}
                              onChange={(e) =>
                                handleAdjItemChange(
                                  index,
                                  "adjustment_quantity",
                                  parseInt(e.target.value) || 0
                                )
                              }
                              className="text-center font-bold h-8 px-1"
                            />
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 shrink-0"
                              onClick={() =>
                                handleAdjItemChange(
                                  index,
                                  "adjustment_quantity",
                                  (item.adjustment_quantity || 0) + 1
                                )
                              }
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="px-4 py-3 w-[120px]">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.unit_cost}
                            onChange={(e) =>
                              handleAdjItemChange(
                                index,
                                "unit_cost",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="text-right"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            placeholder="Reason for this item..."
                            value={item.reason_notes}
                            onChange={(e) =>
                              handleAdjItemChange(
                                index,
                                "reason_notes",
                                e.target.value
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal>

      {/* Serial Selection Modal (for stock adjustment) */}
      {/* Universal Serial Numbers Modal */}
      {activeSerialIndex !== null &&
        newAdjustmentData.items[activeSerialIndex] && (
          <AddSerialsModal
            isOpen={showSerialModal}
            onClose={() => setShowSerialModal(false)}
            productName={
              newAdjustmentData.items[activeSerialIndex]?.product_name
            }
            quantity={Math.abs(
              newAdjustmentData.items[activeSerialIndex]
                ?.adjustment_quantity || 0
            )}
            initialSerials={
              newAdjustmentData.items[activeSerialIndex]?.serial_numbers || []
            }
            availableSerials={
              newAdjustmentData.items[activeSerialIndex]?.available_serials || []
            }
            adjustmentType={
              (newAdjustmentData.items[activeSerialIndex]
                ?.adjustment_quantity || 0) < 0
                ? "removal"
                : "addition"
            }
            onAdjustmentTypeChange={(newType) => {
              const currentQty = Math.max(
                1,
                Math.abs(
                  newAdjustmentData.items[activeSerialIndex]
                    ?.adjustment_quantity || 0
                )
              );
              handleAdjItemChange(
                activeSerialIndex,
                "adjustment_quantity",
                newType === "removal" ? -currentQty : currentQty
              );
            }}
            allowQuantityEdit={true}
            onQuantityChange={(newQty, currentType) => {
              handleAdjItemChange(
                activeSerialIndex,
                "adjustment_quantity",
                currentType === "removal" ? -newQty : newQty
              );
            }}
            onSave={(savedSerials, newQty, currentType) => {
              handleAdjItemChange(
                activeSerialIndex,
                "serial_numbers",
                savedSerials
              );
              if (newQty !== undefined) {
                handleAdjItemChange(
                  activeSerialIndex,
                  "adjustment_quantity",
                  currentType === "removal" ? -newQty : newQty
                );
              }
              setShowSerialModal(false);
            }}
          />
        )}
    </>
  );
};

ProductStockAdjustmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  product: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default ProductStockAdjustmentModal;
